import React from "react";

import Link from "next/link";
import { readableColor } from "polished";
import * as PropTypes from "prop-types";
import { Nav } from "react-bootstrap";
import styled from "styled-components";

import { useStores } from "../../contexts/StoreContext";
import Icon from "../../shared/Icon/Icon";
import breakpoints from "../../shared/constants";

const StyledNavLink = styled(Nav.Link)`
  font-size: ${({ fontSize }) => (fontSize ? `${fontSize}px` : "inherit")};
  padding: 0.5rem 0.5rem;
  color: ${({ active, theme }) =>
    active
      ? readableColor("#ffffff", theme.primaryColor, "#ffffff", false)
      : "#000000"} !important;

  @media (min-width: ${breakpoints.TABLET}) {
    color: ${({ active, theme, $navigationBackgroundColor }) =>
      active
        ? readableColor(
            $navigationBackgroundColor || "#ffffff",
            theme.primaryColor,
            "#ffffff",
            false
          )
        : readableColor($navigationBackgroundColor || "#ffffff")} !important;
  }

  text-decoration: none !important;
  transition: color 0.2s ease-in-out !important;
  display: flex;
  align-items: center;

  svg {
    color: inherit;
  }

  &:focus,
  &:hover {
    border: 0 !important;
  }

  border-bottom: ${({ $isMobile }) =>
    $isMobile ? "1px solid hsla(0,0%,100%,.7)" : "none"};
`;

const StyledChildLink = styled.div`
  font-size: ${({ fontSize }) => (fontSize ? `${fontSize}px` : "inherit")};

  color: ${({ active, theme }) =>
    active
      ? readableColor("#ffffff", theme.primaryColor, "#ffffff", false)
      : "#000000"} !important;

  @media (min-width: ${breakpoints.TABLET}) {
    color: ${({ active, theme, $navigationBackgroundColor }) =>
      active
        ? readableColor(
            $navigationBackgroundColor || "#ffffff",
            theme.primaryColor,
            "#ffffff",
            false
          )
        : readableColor($navigationBackgroundColor || "#ffffff")} !important;
  }
  padding: 0.5rem 0.5rem;

  text-decoration: none !important;
  transition: color 0.2s ease-in-out !important;
  display: flex;
  align-items: center;

  svg {
    color: inherit;
  }

  &:focus,
  &:hover {
    border: 0 !important;
  }
  border-bottom: ${({ $isMobile }) =>
    $isMobile ? "1px solid hsla(0,0%,100%,.7)" : "none"};
`;

function StyledLink(props) {
  const {
    active,
    as,
    children,
    className,
    eventKey,
    href,
    fontSize,
    navigationBackgroundColor,
    page,
    isMobile,
    onClick,
  } = props;

  const { navigationStore } = useStores();
  const { iconsById } = navigationStore;
  const { attributes } = iconsById[page.iconId] || {};

  const iconPaths = attributes?.iconProps.path || [];
  const viewBox = attributes?.iconProps.viewBox;

  const isExternal = href?.includes("http") ?? false;

  const link = (
    <StyledNavLink
      $navigationBackgroundColor={navigationBackgroundColor}
      active={active}
      className={className}
      fontSize={fontSize}
      href={href}
      $isMobile={isMobile}
    >
      <Icon
        className="me-3"
        optionalPath={iconPaths}
        optionalViewBox={viewBox}
        size={20}
      />
      {children}
    </StyledNavLink>
  );

  if (onClick) {
    return (
      <StyledChildLink
        $navigationBackgroundColor={navigationBackgroundColor}
        className={className}
        fontSize={fontSize}
        $isMobile={isMobile}
        onClick={onClick}
      >
        <Icon
          className="me-3"
          optionalPath={iconPaths}
          optionalViewBox={viewBox}
          size={20}
        />
        {children}
      </StyledChildLink>
    );
  }
  if (isExternal) {
    return link;
  }

  return (
    <Link as={as} eventKey={eventKey} href={href || "/"} passHref legacyBehavior>
      {link}
    </Link>
  );
}

StyledLink.defaultProps = {
  as: null,
  children: null,
  className: null,
  fontSize: null,
  isMobile: false,
  navigationBackgroundColor: "#ffffff",
  onClick: null,
  page: {},
};

StyledLink.propTypes = {
  active: PropTypes.bool.isRequired,
  as: PropTypes.element,
  children: PropTypes.string,
  className: PropTypes.string,
  eventKey: PropTypes.string.isRequired,
  fontSize: PropTypes.number,
  href: PropTypes.string.isRequired,
  isMobile: PropTypes.bool,
  navigationBackgroundColor: PropTypes.string,
  onClick: PropTypes.func,
  page: PropTypes.shape({
    iconId: PropTypes.number,
  }),
};

export default StyledLink;
